import React, { useState, useMemo, useEffect } from 'react';
import './App.css';
import * as Calc from './lib/calc';

const fmt = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMoney = (n: number) => (n >= 0 ? '+' : '') + '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (n: number) => (n >= 0 ? '+' : '') + n.toFixed(2) + '%';

export default function App() {
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [view, setView] = useState<'calc' | 'history' | 'profile'>('calc');
  const [currentUser, setCurrentUser] = useState<Calc.UserProfile | null>(null);
  const [allUsers, setAllUsers] = useState<Calc.UserProfile[]>([]);
  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginError, setLoginError] = useState('');
  const [trades, setTrades] = useState<Calc.Trade[]>([]);
  const [activeTrade, setActiveTrade] = useState<Calc.Trade>({
    id: Date.now().toString(), user: '', startDate: Date.now(), type: 'CALL',
    ticker: '', expiration: '', strike: '', lots: [], exits: [], status: 'OPEN'
  });
  const [mode, setMode] = useState<'BUY' | 'SELL'>('BUY');
  const [inputC, setInputC] = useState('');
  const [inputP, setInputP] = useState('');
  const [bid, setBid] = useState('');

  useEffect(() => {
    const savedTrades = localStorage.getItem('mf_trades');
    const savedUsers = localStorage.getItem('mf_users');
    const savedActiveUser = localStorage.getItem('mf_active_user');
    if (savedTrades) try { setTrades(JSON.parse(savedTrades)); } catch (e) { console.error(e); }
    if (savedUsers) {
      try { 
        const parsedUsers = JSON.parse(savedUsers);
        setAllUsers(parsedUsers);
        if (savedActiveUser) {
          const found = parsedUsers.find((u: Calc.UserProfile) => u.username === savedActiveUser);
          if (found) setCurrentUser(found);
        }
      } catch (e) { console.error(e); }
    }
    document.body.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => { localStorage.setItem('mf_trades', JSON.stringify(trades)); }, [trades]);
  useEffect(() => { localStorage.setItem('mf_users', JSON.stringify(allUsers)); }, [allUsers]);
  useEffect(() => {
    if (currentUser) localStorage.setItem('mf_active_user', currentUser.username);
    else localStorage.removeItem('mf_active_user');
  }, [currentUser]);

  const myTrades = useMemo(() => trades.filter(t => currentUser && t.user === currentUser.username), [trades, currentUser]);
  const openTrades = useMemo(() => myTrades.filter(t => t.status === 'OPEN').sort((a,b) => b.startDate - a.startDate), [myTrades]);
  const closedTrades = useMemo(() => myTrades.filter(t => t.status === 'CLOSED').sort((a,b) => b.startDate - a.startDate), [myTrades]);
  const stats = useMemo(() => Calc.getStats(myTrades), [myTrades]);
  const totalWins = useMemo(() => closedTrades.filter(t => Calc.getTotals(t).realizedPnL > 0).length, [closedTrades]);
  const levelInfo = useMemo(() => Calc.getLevelInfo(totalWins), [totalWins]);
  const progressPct = Math.min(100, (levelInfo.progressInLevel / levelInfo.currentGoal) * 100);

  const totals = useMemo(() => Calc.getTotals(activeTrade), [activeTrade]);
  const numBid = parseFloat(bid);
  const isPotentialFree = numBid > 0 && totals.remainingCost > 0 && (numBid * totals.remaining * 100) >= totals.remainingCost;
  const scenarios = useMemo(() => Calc.getScenarios(totals.remaining, totals.remainingCost, totals.avgEntry, numBid || undefined), [totals, numBid]);
  const freeRunnersNow = totals.remainingCost <= 0 ? totals.remaining : Math.max(0, totals.remaining - (numBid > 0 ? Math.ceil(totals.remainingCost / (numBid * 100)) : totals.remaining));

  // --- USE NEW STOP LOSS CALC ---
  const stopLossPrice = useMemo(() => Calc.calculateStopLoss(totals.avgEntry), [totals.avgEntry]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    if (!loginUser.trim() || !loginPass.trim()) return setLoginError('Enter Username and Password');
    const cleanUser = loginUser.trim().toUpperCase();
    const existing = allUsers.find(u => u.username === cleanUser);
    if (existing) {
      if (existing.password === loginPass) { setCurrentUser(existing); setLoginUser(''); setLoginPass(''); }
      else setLoginError('Incorrect Password');
    } else {
      if (window.confirm(`Create profile for "${cleanUser}"?`)) {
        const newUser: Calc.UserProfile = { username: cleanUser, password: loginPass, joinedAt: Date.now() };
        setAllUsers([...allUsers, newUser]); setCurrentUser(newUser); setLoginUser(''); setLoginPass('');
      }
    }
  };

  const handleLogout = () => { setCurrentUser(null); setView('calc'); handleClear(); };

  const handleExecute = () => {
    if (!currentUser) return;
    const qty = parseInt(inputC); const px = parseFloat(inputP);
    if (!qty || !px) return;
    const newTrade = { ...activeTrade, user: currentUser.username };
    if (mode === 'BUY') newTrade.lots = [...newTrade.lots, { contracts: qty, premium: px, date: Date.now() }];
    else {
      if (qty > totals.remaining) return alert(`Limit: ${totals.remaining} contracts.`);
      newTrade.exits = [...newTrade.exits, { id: Date.now().toString(), contracts: qty, price: px, date: Date.now() }];
    }
    setActiveTrade(newTrade); setInputC(''); setInputP('');
    const exists = trades.find(t => t.id === newTrade.id);
    if (exists) setTrades(prev => prev.map(t => t.id === newTrade.id ? newTrade : t));
  };

  const handleSave = () => {
    if (!currentUser || !activeTrade.ticker || !activeTrade.expiration || !activeTrade.strike) return alert('Fill Ticker, Exp, Strike');
    const tradeToSave = { ...activeTrade, user: currentUser.username };
    setTrades(prev => {
      const exists = prev.find(t => t.id === tradeToSave.id);
      if (exists) return prev.map(t => t.id === tradeToSave.id ? tradeToSave : t);
      return [tradeToSave, ...prev];
    });
    alert('Trade Saved');
  };

  const handleFinalize = () => {
    if (!currentUser) return;
    if (totals.remaining > 0 && !window.confirm(`Close with ${totals.remaining} left?`)) return;
    const closedTrade = { ...activeTrade, user: currentUser.username, status: 'CLOSED' as const };
    setTrades(prev => {
      const exists = prev.find(t => t.id === closedTrade.id);
      if (exists) return prev.map(t => t.id === closedTrade.id ? closedTrade : t);
      return [closedTrade, ...prev];
    });
    handleClear(); setView('history');
  };

  const handleLoad = (t: Calc.Trade) => { setActiveTrade(t); setView('calc'); };
  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (window.confirm('Delete?')) { setTrades(prev => prev.filter(t => t.id !== id)); if (activeTrade.id === id) handleClear(); }
  };

  const handleClear = () => {
    setActiveTrade({ id: Date.now().toString(), user: currentUser?.username || '', startDate: Date.now(), type: 'CALL', ticker: '', expiration: '', strike: '', lots: [], exits: [], status: 'OPEN' });
    setBid(''); setInputC(''); setInputP('');
  };

  if (!currentUser) {
    return (
      <div className="login-container">
        <form className="login-card" onSubmit={handleLogin}>
          <div style={{fontWeight: 900, fontSize: '2rem', marginBottom: '10px'}}>MAKE IT <span style={{ color: 'var(--accent-green)' }}>FREE</span></div>
          <input className="login-input" value={loginUser} onChange={e => setLoginUser(e.target.value.toUpperCase())} placeholder="USERNAME" autoFocus />
          <input className="login-input" type="password" value={loginPass} onChange={e => setLoginPass(e.target.value)} placeholder="PASSWORD" />
          {loginError && <div style={{color: 'var(--accent-red)', fontSize: '0.9rem'}}>{loginError}</div>}
          <button className="btn-login" type="submit">ENTER DASHBOARD</button>
        </form>
      </div>
    );
  }

  return (
    <>
      <div className="hero-container"><div className="hero-overlay" /></div>
      <div className="app-container">
        <header className="app-header">
          <div className="header-spacer"></div>
          <div className="header-title">MAKE IT <span style={{ color: 'var(--accent-green)' }}>FREE</span></div>
          <div className="theme-toggle-wrapper">
             <div style={{display:'flex', alignItems:'center', gap:'10px'}}>
               <span style={{fontSize:'0.7rem', color:'var(--accent-green)', fontWeight:'bold'}}>{currentUser.username}</span>
               <button className="btn-logout" onClick={handleLogout}>LOGOUT</button>
             </div>
          </div>
        </header>

        <nav className="nav-tabs">
          <button className={`nav-tab ${view === 'calc' ? 'active' : ''}`} onClick={() => setView('calc')}>CALCULATOR</button>
          <button className={`nav-tab ${view === 'history' ? 'active' : ''}`} onClick={() => setView('history')}>HISTORY</button>
          <button className={`nav-tab ${view === 'profile' ? 'active' : ''}`} onClick={() => setView('profile')}>PROFILE</button>
        </nav>

        <main className="main-grid">
          {view === 'calc' && (
            <>
              <section>
                <div className={`card ${isPotentialFree ? 'glow-success' : ''}`}>
                  <div className="toggle-group">
                    <button className={`toggle-opt ${activeTrade.type === 'CALL' ? 'active-call' : ''}`} onClick={() => setActiveTrade({...activeTrade, type: 'CALL'})}>CALL</button>
                    <button className={`toggle-opt ${activeTrade.type === 'PUT' ? 'active-put' : ''}`} onClick={() => setActiveTrade({...activeTrade, type: 'PUT'})}>PUT</button>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                    <div className="input-group"><label>Ticker</label><input value={activeTrade.ticker} onChange={e => setActiveTrade({...activeTrade, ticker: e.target.value.toUpperCase()})} placeholder="SPY" /></div>
                    <div className="input-group"><label>Exp</label><input value={activeTrade.expiration} onChange={e => setActiveTrade({...activeTrade, expiration: e.target.value})} placeholder="2/21" /></div>
                    <div className="input-group"><label>Strike</label><input value={activeTrade.strike} onChange={e => setActiveTrade({...activeTrade, strike: e.target.value})} placeholder="500" /></div>
                  </div>
                  <div style={{background: 'var(--bg-input)', padding: '15px', borderRadius: '8px', border: '1px solid var(--border-subtle)', marginBottom: '15px'}}>
                    <div style={{display:'flex', gap:'10px', marginBottom:'10px'}}>
                      <button style={{flex:1, padding:'8px', borderRadius:'4px', border:'none', background: mode==='BUY'?'var(--accent-green)':'var(--bg-surface)', color: mode==='BUY'?'#000':'var(--text-secondary)', fontWeight:'bold'}} onClick={()=>setMode('BUY')}>BUY</button>
                      <button style={{flex:1, padding:'8px', borderRadius:'4px', border:'none', background: mode==='SELL'?'var(--accent-red)':'var(--bg-surface)', color: mode==='SELL'?'#fff':'var(--text-secondary)', fontWeight:'bold'}} onClick={()=>setMode('SELL')}>SELL</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                      <div className="input-group"><label>Contracts</label><input type="number" step="1" value={inputC} onChange={e => setInputC(e.target.value)} /></div>
                      <div className="input-group"><label>Price</label><input type="number" step="0.01" value={inputP} onChange={e => setInputP(e.target.value)} /></div>
                    </div>
                    {mode === 'BUY' ? <button className="btn-play" onClick={handleExecute}>ADD BUY</button> : <button className="btn-sell" onClick={handleExecute}>EXECUTE SELL</button>}
                  </div>
                  {totals.totalBought > 0 && (
                    <>
                      <div style={{fontSize:'0.8rem', color:'var(--text-secondary)', marginBottom:'5px'}}>TRANSACTION LOG</div>
                      <table className="ledger-table">
                        <tbody>
                          {activeTrade.lots.map((l, i) => (<tr key={'buy'+i} className="row-buy"><td>BUY</td><td>{l.contracts} @ ${fmt(l.premium)}</td><td>${fmt(l.contracts * l.premium * 100)}</td></tr>))}
                          {activeTrade.exits.map((e, i) => (<tr key={'sell'+i} className="row-sell"><td>SELL</td><td>{e.contracts} @ ${fmt(e.price)}</td><td>${fmt(e.contracts * e.price * 100)}</td></tr>))}
                        </tbody>
                      </table>
                      <div style={{marginTop:'20px', display:'flex', gap:'10px'}}>
                        <button className="btn-save" onClick={handleSave}>SAVE TRADE</button>
                        <button className="btn-finalize" onClick={handleFinalize}>CLOSE TRADE</button>
                      </div>
                    </>
                  )}
                  <button className="btn-clear" onClick={handleClear}>NEW TRADE</button>
                </div>
                {totals.totalBought > 0 && (
                  <div className={`card ${isPotentialFree ? 'glow-success' : ''}`} style={{ marginTop: '20px' }}>
                    <div className="input-group">
                      <label style={{ color: isPotentialFree ? 'var(--accent-green)' : 'inherit' }}>Current Bid {isPotentialFree && '(COVERS COST!)'}</label>
                      <input type="number" step="0.01" value={bid} onChange={e => setBid(e.target.value)} />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', margin: '10px 0' }}><span>Remaining</span><strong>{totals.remaining} contracts</strong></div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', margin: '10px 0' }}><span>Avg Entry</span><strong>${fmt(totals.avgEntry)}</strong></div>
                    
                    {/* UPDATED STOP LOSS LABEL */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', margin: '10px 0', borderTop: '1px dashed var(--border-subtle)', paddingTop: '10px' }}>
                        <span style={{color: 'var(--accent-red)', fontWeight: 'bold'}}>STOP LOSS (-70%)</span>
                        <strong style={{color: 'var(--accent-red)'}}>${fmt(stopLossPrice)}</strong>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', margin: '10px 0', borderTop: '1px solid var(--border-subtle)', paddingTop: '10px' }}>
                      <span>Realized P&L</span>
                      <div>
                        <strong className={totals.realizedPnL >= 0 ? 'pnl-positive' : 'pnl-negative'}>{fmtMoney(totals.realizedPnL)}</strong>
                        <span style={{marginLeft: '8px', fontSize: '0.8rem'}} className={totals.pnlPct >= 0 ? 'pnl-positive' : 'pnl-negative'}>({fmtPct(totals.pnlPct)})</span>
                      </div>
                    </div>
                  </div>
                )}
              </section>
              <section>
                {totals.totalBought === 0 ? (
                  <div className="instruction-text">
                    <h3>SPEED MATTERS!</h3>
                    <p>Once you add your <strong>Buy Order</strong>, press <strong>ADD BUY</strong>.</p>
                    <p>Find the exit prices needed to <strong>MAKE IT FREE</strong>.</p>
                    <div className="instruction-footer">GET A LONGTERM, AVOID STUPIDITY and MAKE IT FREE!</div>
                  </div>
                ) : (
                  <>
                    {totals.remaining > 0 ? (
                      <>
                        <div className="card scoreboard">
                          {totals.remainingCost <= 0 ? <div style={{color:'var(--accent-green)', fontWeight:'bold', fontSize:'1.2rem'}}>🎉 RISK FREE! 🎉</div> : <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>FREE RUNNERS IF SOLD NOW</div>}
                          <div className="hero-number" style={{ color: freeRunnersNow > 0 ? 'var(--accent-green)' : 'inherit' }}>{freeRunnersNow}</div>
                        </div>
                        <div className="card" style={{ marginTop: '20px', padding: '0' }}>
                          <table className="scenarios-table">
                            <thead><tr><th>Free</th><th>Sell</th><th>Limit</th><th>Gain</th></tr></thead>
                            <tbody>
                              {scenarios.map(s => (
                                <tr key={s.freeGoal} className={numBid >= s.limitPriceShare ? 'achieved' : ''}>
                                  <td>{s.freeGoal}</td><td>{s.sellQty}</td><td>${fmt(s.limitPriceShare)}</td><td>{s.gainNeededPct}%</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    ) : (
                      <div className="card scoreboard">
                        <div style={{color:'var(--text-secondary)'}}>TRADE CLOSED</div>
                        <div className="hero-number" style={{ color: totals.realizedPnL >= 0 ? 'var(--accent-green)' : 'var(--accent-red)' }}>{fmtMoney(totals.realizedPnL)}</div>
                      </div>
                    )}
                  </>
                )}
              </section>
            </>
          )}

          {view === 'history' && (
            <div style={{ gridColumn: 'span 2' }}>
              <div className="summary-grid">
                <div className="summary-box"><div className="summary-label">Today</div><div className={`summary-val ${stats.dayPnL >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>{fmtMoney(stats.dayPnL)}</div></div>
                <div className="summary-box"><div className="summary-label">Week</div><div className={`summary-val ${stats.weekPnL >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>{fmtMoney(stats.weekPnL)}</div></div>
                <div className="summary-box"><div className="summary-label">Month</div><div className={`summary-val ${stats.monthPnL >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>{fmtMoney(stats.monthPnL)}</div></div>
                <div className="summary-box"><div className="summary-label">All Time</div><div className={`summary-val ${stats.allTimePnL >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>{fmtMoney(stats.allTimePnL)}</div></div>
              </div>
              <div className="history-section-title">Active Positions</div>
              <div className="history-list">
                {openTrades.map(t => {
                  const s = Calc.getTotals(t);
                  return (
                    <div key={t.id} className="card history-card status-OPEN" onClick={() => handleLoad(t)}>
                      <div style={{display:'flex', justifyContent:'space-between'}}>
                        <strong>{t.type} {t.ticker} {t.expiration} {t.strike}</strong>
                        <div><span className={s.realizedPnL >= 0 ? 'pnl-positive' : 'pnl-negative'}>{fmtMoney(s.realizedPnL)}</span> <span style={{fontSize:'0.8rem'}}>({fmtPct(s.pnlPct)})</span></div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="history-section-title">Trade History</div>
              <div className="history-list">
                {closedTrades.map(t => {
                  const s = Calc.getTotals(t);
                  return (
                    <div key={t.id} className="card history-card status-CLOSED" onClick={() => handleLoad(t)}>
                      <div style={{display:'flex', justifyContent:'space-between'}}>
                         <strong>{t.ticker} {t.expiration} {t.strike}</strong>
                         <div><span className={s.realizedPnL >= 0 ? 'pnl-positive' : 'pnl-negative'}>{fmtMoney(s.realizedPnL)}</span> <span style={{fontSize:'0.8rem'}}>({fmtPct(s.pnlPct)})</span></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {view === 'profile' && (
            <div style={{ gridColumn: 'span 2' }}>
               <div className="card">
                  <div className="profile-header">
                    <div style={{fontSize:'0.7rem', color:'var(--text-secondary)'}}>TRADER</div>
                    <div style={{fontSize:'2rem', fontWeight:'900'}}>{currentUser.username}</div>
                    <div className="level-badge">LVL {levelInfo.currentLevel} • {levelInfo.rankTitle}</div>
                  </div>
                  <div className="level-container">
                    <div style={{display:'flex', justifyContent:'space-between', marginBottom:'5px'}}><span>WINS</span><span>{levelInfo.progressInLevel}/{levelInfo.currentGoal}</span></div>
                    <div className="level-bar-bg"><div className="level-bar-fill" style={{width: `${progressPct}%`}}></div></div>
                    <div style={{textAlign:'center', marginTop:'10px', fontSize:'0.8rem', color:'var(--text-secondary)'}}>Target: {levelInfo.nextRankTitle}</div>
                  </div>
                  <div style={{textAlign:'center', marginTop:'30px'}}><button className="btn-logout" onClick={handleLogout}>LOGOUT</button></div>
               </div>
            </div>
          )}
        </main>
        <footer className="app-footer"><strong>NOT FINANCIAL ADVICE.</strong></footer>
      </div>
    </>
  );
}